import { useTheme } from "@mui/material";
import { NodeProps } from "reactflow";

import StandardNode from "components/Flow/Nodes/StandardNode";
import StandardNodeHeader from "components/Flow/Nodes/StandardNode/StandardNodeHeader";
import StandardNodeFunctionality from "components/Flow/Nodes/StandardNode/StandardNodeFunctionality";
import StandardNodeFooter from "components/Flow/Nodes/StandardNode/StandardNodeFooter";

import { functionalityValuesAPI } from "api/Community/DiscipleshipPathwayAPI";

export interface DiscipleshipPathwayNodeData {
  name: string;

  // Optional display fields (match CommunityPathwayNode patterns)
  category?: string;
  type?: string;
  colorPrimary?: string;
  colorText?: string;

  // What StandardNodeFunctionality expects
  functionalities?: any[];
  people?: any[];
  peopleCount?: number;
  addresses?: any[];
  addressesCount?: number;
  itemTypes?: "People" | "Addresses" | "Both";

  // API object passed down to StandardNodeFunctionality
  communityNodeAPI: any;

  // Used to update local UI state after API changes
  updateNodeFunctionality: (
    nodeId: string,
    functionalityId: number,
    field: string,
    value: any,
    valueId?: number | null
  ) => void;
}

/**
 * DiscipleshipPathwayNode
 * ----------------------
 * React Flow v11–compliant discipleship pathway node
 * Mirrors CommunityPathwayNode behavior for functionalities.
 */
const DiscipleshipPathwayNode = ({
  id,
  data,
  selected,
}: NodeProps<DiscipleshipPathwayNodeData>) => {
  const theme = useTheme();

  const handleFunctionalityUpdateField = async (
    functionalityId: number,
    valueId: number | null, // may be null
    field: string,
    value: any
  ) => {
    let newValueId = valueId;

    if (!valueId) {
      // Create missing functionality_value record
      const created = await functionalityValuesAPI.create({
        node: Number(id), // ReactFlow node id maps to backend node id
        functionality_type: Number(functionalityId),
        value: value,
      });

      newValueId = created.id;
    } else {
      // Update existing functionality_value
      await functionalityValuesAPI.updateField(Number(valueId), field, value);
    }

    // Update UI immediately
    data.updateNodeFunctionality(id, functionalityId, field, value, newValueId);
  };

  const borderColor = data.colorPrimary ?? theme.custom.nodes.header.main.dark;
  const headerBackground = data.colorPrimary ?? theme.custom.nodes.header.main.dark;
  const headerTextColor = data.colorText ?? theme.custom.nodes.text.primary.dark;
  
  return (
    <StandardNode
      selected={selected}
      borderColor={borderColor}
      backgroundColor={theme.custom.nodes.background.main.dark}
      header={
        <StandardNodeHeader
          title={data.name}
          subtitle={data.category}
          secondaryText={data.type}
          backgroundColor={headerBackground}
          textColor={headerTextColor}
          secondaryTextColor={headerTextColor}
        />
      }
      functionalities={
        <StandardNodeFunctionality
          functionalities={data.functionalities || []}
          apiFunctionalityUpdateFieldFn={handleFunctionalityUpdateField}
          nodeId={Number(id)}
          nodeAPI={data.communityNodeAPI}
          currentPeople={data.people ?? []}
          currentAddresses={data.addresses ?? []}
        />
      }
      footer={
        <StandardNodeFooter
          centerSection={{
            value: data.peopleCount ?? 0,
            label: "People",
          }}
        />
      }
    />
  );
};

export default DiscipleshipPathwayNode;
